import React, { useState, useEffect } from "react";
import { StoreProvider, useStore } from "./store/Store";
import Navbar from "./components/Navbar";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import AccountPage from "./pages/AccountPage";
import OrdersPage from "./pages/OrdersPage";
import WishlistPage from "./pages/WishlistPage";
import AdminPanel from "./pages/AdminPanel";
import AdminLoginPage from "./pages/AdminLoginPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ReturnsPage from "./pages/ReturnsPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import FAQPage from "./pages/FAQPage";

const KNOWN_PAGES = ["home","shop","product","cart","checkout","account","orders","wishlist","adminlogin","admin","terms","privacy","returns","contact","about","faq"];

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("Matchwell app crashed:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ position:"fixed", inset:0, background:"white", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center", zIndex:99999 }}>
          <div style={{ fontSize:52, marginBottom:16 }}>😕</div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:"#1a1a2e", marginBottom:8 }}>Something went wrong</h2>
          <p style={{ fontSize:13, color:"#999", marginBottom:24, maxWidth:320 }}>We hit a technical issue. Please reload the page and try again.</p>
          <button onClick={()=>window.location.reload()} style={{ background:"#EC4899", color:"white", border:"none", padding:"12px 32px", borderRadius:50, fontSize:14, fontWeight:700, cursor:"pointer" }}>
            🔄 Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function LoadingScreen() {
  const { logoUrl } = useStore();
  return (
    <div style={{ position:"fixed", inset:0, background:"linear-gradient(135deg,#1a1a2e,#2d1b69)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", zIndex:99999 }}>
      <style>{`
        @keyframes butterflyFloat { 0%,100%{transform:translateY(0) rotate(-5deg) scale(1);} 50%{transform:translateY(-20px) rotate(5deg) scale(1.1);} }
        @keyframes loadBar { 0%{width:0%;} 100%{width:100%;} }
        @keyframes shimmer { 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(20px);} to{opacity:1;transform:translateY(0);} }
      `}</style>
      <div style={{ animation:"butterflyFloat 2s ease-in-out infinite", marginBottom:24 }}>
        <img src={logoUrl}
          alt="Matchwell" style={{ width:100, height:100, objectFit:"contain", filter:"drop-shadow(0 0 20px rgba(236,72,153,0.6))" }}
          onError={e=>e.target.style.display="none"}/>
      </div>
      <div style={{ fontFamily:"'Noto Serif Devanagari',serif", fontSize:32, fontWeight:900, color:"white", marginBottom:4, animation:"fadeInUp 0.5s ease" }}>मॅचवेल</div>
      <div style={{ fontSize:10, letterSpacing:4, textTransform:"uppercase", color:"#EC4899", marginBottom:32, animation:"fadeInUp 0.5s ease 0.1s both" }}>Est. 1979 · Aurangabad</div>
      <div style={{ width:200, height:3, background:"rgba(255,255,255,0.1)", borderRadius:2, overflow:"hidden" }}>
        <div style={{ height:"100%", background:"linear-gradient(90deg,#EC4899,#7C3AED,#EC4899)", backgroundSize:"200%", animation:"loadBar 1.5s ease-in-out forwards, shimmer 1.5s linear infinite", borderRadius:2 }}/>
      </div>
    </div>
  );
}

function AppInner() {
  const { dbReady } = useStore();
  const [page, setPage]         = useState("home");
  const [pid, setPid]           = useState(null);
  const [shopCat, setShopCat]   = useState("All");
  const [searchQ, setSearchQ]   = useState("");
  const [minTimeDone, setMinTimeDone] = useState(false);
  const [pageAnim, setPageAnim] = useState(false);

  // Show the splash for at least ~1.2s (feels intentional, not a flash),
  // AND until real data has actually loaded from Supabase — whichever is longer.
  useEffect(()=>{
    const t = setTimeout(()=>setMinTimeDone(true), 1200);
    return ()=>clearTimeout(t);
  },[]);
  const loading = !minTimeDone || !dbReady;

  const onNav = (pg, param, sq) => {
    window.scrollTo(0,0);
    const target = KNOWN_PAGES.includes(pg) ? pg : "home"; // unknown page → safe fallback instead of a blank screen
    if (target==="product") { setPid(param); setPage("product"); }
    else if (target==="shop") { setShopCat(param||"All"); if(sq) setSearchQ(sq); setPage("shop"); }
    else setPage(target);
    window.history.pushState({pg:target,param},"",`#${target}`);
  };

  useEffect(()=>{
    const handlePop = (e) => {
      const state = e.state;
      if (state?.pg && KNOWN_PAGES.includes(state.pg)) {
        if (state.pg==="product") { setPid(state.param); setPage("product"); }
        else if (state.pg==="shop") { setShopCat(state.param||"All"); setPage("shop"); }
        else setPage(state.pg);
        window.scrollTo(0,0);
      } else { setPage("home"); window.scrollTo(0,0); }
    };
    window.addEventListener("popstate", handlePop);
    return ()=>window.removeEventListener("popstate", handlePop);
  },[]);

  useEffect(()=>{
    let startY = 0;
    const onTouchStart = (e) => { startY = e.touches[0].clientY; };
    const onTouchEnd = (e) => {
      const diff = e.changedTouches[0].clientY - startY;
      if (diff > 100 && window.scrollY === 0) {
        if (navigator.vibrate) navigator.vibrate(50);
        window.location.reload();
      }
    };
    window.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchend", onTouchEnd);
    return ()=>{ window.removeEventListener("touchstart", onTouchStart); window.removeEventListener("touchend", onTouchEnd); };
  },[]);

  const showNav = page !== "adminlogin" && page !== "admin";

  if (loading) return <LoadingScreen/>;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=DM+Sans:wght@300;400;500;600;700;800&family=Noto+Serif+Devanagari:wght@700;900&display=swap');
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'DM Sans',sans-serif; background:white; color:#1a1a2e; -webkit-overflow-scrolling:touch; }
        input,select,textarea,button { font-family:'DM Sans',sans-serif; }
        ::-webkit-scrollbar { width:4px; height:4px; }
        ::-webkit-scrollbar-track { background:#fafafa; }
        ::-webkit-scrollbar-thumb { background:#e5e5e5; border-radius:2px; }
        @keyframes ticker { to { transform:translateX(-50%); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px);} to{opacity:1;transform:translateY(0);} }
        @keyframes fadeIn { from{opacity:0;} to{opacity:1;} }
        @keyframes slideUp { from{opacity:0;transform:translateY(40px);} to{opacity:1;transform:translateY(0);} }
        @keyframes pulse { 0%,100%{transform:scale(1);} 50%{transform:scale(1.05);} }
        @keyframes shimmerAnim { 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }
        .page-enter { animation: fadeIn 0.3s ease; }
        .page-top-pad { padding-top: 64px; }
        .mobile-zero-top { }
        .shop-stickybar { top: 64px; }
        .shop-sidebar { top: 128px; height: calc(100vh - 128px); }
        @media(max-width:900px) {
          body { padding-bottom:64px; }
          .desk-nav { display:none !important; }
          #mobNav { display:flex !important; }
          .nav-account-icon { display:none !important; }
          .page-top-pad { padding-top: 0 !important; }
          .mobile-zero-top { padding-top: 0 !important; }
          .shop-stickybar { top: 0 !important; }
          .shop-sidebar { top: 64px !important; height: calc(100vh - 64px) !important; }
          .product-detail-grid { grid-template-columns:1fr !important; }
          .cart-layout-grid { grid-template-columns:1fr !important; }
          .checkout-grid { grid-template-columns:1fr !important; }
          .cart-layout-grid > div:last-child { position:static !important; }
          .checkout-grid > div:last-child { position:static !important; }
        }
      `}</style>

      {showNav && <Navbar page={page} onNav={onNav}/>}

      <div className="page-enter" style={{ opacity:pageAnim?0:1, transition:"opacity 0.2s ease" }}>
        {page==="home"       && <HomePage     onNav={onNav}/>}
        {page==="shop"       && <ShopPage     onNav={onNav} initCat={shopCat} searchQ={searchQ}/>}
        {page==="product"    && <ProductPage  onNav={onNav} pid={pid}/>}
        {page==="cart"       && <CartPage     onNav={onNav}/>}
        {page==="checkout"   && <CheckoutPage onNav={onNav}/>}
        {page==="account"    && <AccountPage  onNav={onNav}/>}
        {page==="orders"     && <OrdersPage   onNav={onNav}/>}
        {page==="wishlist"   && <WishlistPage onNav={onNav}/>}
        {page==="adminlogin" && <AdminLoginPage onNav={onNav}/>}
        {page==="admin"      && <AdminPanel   onNav={onNav}/>}
        {page==="terms"      && <TermsPage    onNav={onNav}/>}
        {page==="privacy"    && <PrivacyPage  onNav={onNav}/>}
        {page==="returns"    && <ReturnsPage  onNav={onNav}/>}
        {page==="contact"    && <ContactPage  onNav={onNav}/>}
        {page==="about"      && <AboutPage    onNav={onNav}/>}
        {page==="faq"        && <FAQPage      onNav={onNav}/>}
      </div>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <StoreProvider>
        <AppInner/>
      </StoreProvider>
    </ErrorBoundary>
  );
}
